import { ApiValidationWarning } from './api-warning.model';
import { DisclosurePackageDetail } from './disclosure-package-detail.model';
import { DisclosureApplication } from './disclosure-type.model';

export interface LoanFileSaveResponse {
  loanFile: LoanApplicationModel;
  validationWarnings: ApiValidationWarning[];
  success: boolean;
}

export interface LoanApplicationModel {
  _LTV: number;
  antiSteeringHighestRate?: any;
  antiSteeringLowestRate?: any;
  antiSteeringTotalClosingCostsAtHighestRate?: any;
  antiSteeringTotalClosingCostsAtLowestRate?: any;
  applicationDate?: any;
  appraisedValue: number;
  baseLoan: number;
  borrowers: Borrower[];
  cA885PaymentScenario?: CA885PaymentScenariosModel;
  complianceCheckRunDate?: any;
  complianceDeliveryDeadline?: any;
  complianceOFACPassed?: any;
  createdDateTime?: any;
  creditFirstIssuedDate?: Date;
  creditReportDate?: any;
  creditReportFee: number;
  deliveryMethod?: any;
  disclosureInfos: any[];
  disclosureApplications?: DisclosureApplication[];
  feesExpires?: any;
  feesLastRun?: any;
  fhaInfo?: FHAInfo;
  vaInfo?: VAInfo;
  fileDataId: number;
  fileName: string;
  initialDeliveryDateAndTime?: any;
  intRate: number;
  isHPML: string;
  lastDisclosed?: any;
  lastRunFees?: any;
  loanAmount?: any;
  loanProgramCode: string;
  loanProgramName: string;
  loanPurpose: string;
  lockFloating: boolean;
  miMethod: string;
  miMonthlyPrem?: any;
  mipffPerc?: any;
  mortgageType: string;
  monthlyMortgagePayment?: number;
  occupancyType: string;
  orderedDateTime?: any;
  propertyState: string;
  purPrice: number;
  qmTestResult: string;
  refiPurpAU: string;
  refiPurpNameOV: string;
  refiTypeFHA: string;
  refiTypeURLAOV: string;
  refiTypeVA: string;
  stateCodeNA: boolean;
  userName?: any;
  vaCashOutRefiType: string;
  supplemental: Supplemental;
  ofacScanComplete: boolean;
  validationWarnings?: ApiValidationWarning[];
  deferredForInitialDisclosure?: boolean;
  newConstruction: string;
  vaFundingFeeCategory: string;
  vaOwnershipType: string;
  vaSplitFee: string;
  lastClosingCorpUpdateDate: string;
  feeUpdateRequired: boolean;
  isTxSpouseSectionValid?: boolean;
  packages?: DisclosurePackageDetail[];
}

export interface CA885PaymentScenariosModel {
  cA885FixedIntRate?: number;
  cA885IntOnlyFirst5IntRate?: number;
  cA885FiveOneARMIntRate?: number;
  cA885IntOnlyFirst5ARMIntRate?: number;
}

export interface PreviousFHAMortgage {
  address?: Address;
  origLoan?: string;
  salesPrice?: string;
  toBeSold?: boolean;
}

export interface FHAInfo {
  fhaLenderId?: string;
  financialInterestInSubdivision?: any;
  hasPreviousFhaMortgage?: boolean;
  previousFhaMortgage?: PreviousFHAMortgage;
  lenderAddress?: Address;
  lenderCompany?: string;
  subdivisionDetails?: string;
}

export interface Address {
  city?: string;
  state?: string;
  street?: string;
  zip?: string;
}

export interface Supplemental {
  sections: Section[];
}

export interface Section {
  sectionCode: string;
  sectionDisplayname: string;
  stateCodes: string[];
  sectionOrdinal?: number;
  prompts: Prompt[];
  data?: Record<string, any>;
}

export interface SaveDisclosuresRequest {
  fileDataId: number;
  newConstruction?: ExtendedField<string>;
  vaSplitFee?: ExtendedField<boolean>;
  supplemental?: Supplemental;
}

export interface ExtendedField<T> {
  id?: number;
  value?: T;
  isDeleted?: boolean;
}

export enum ExtendedFieldValue {
  Yes = 'Yes',
  No = 'No',
  NotAssigned = 'NotAssigned',
}

type TypedPrompt<Type extends PromptType, T = string> = {
  promptCode: string;
  promptDisplayname: string;
  promptSelectionOptions?: string[];
  promptSelectionOptionsDisplay: { name: string; value: string }[];
  promptOrdinal: number;
  isEditable: boolean;
  length?: number;
} & {
  promptType: Type;
  promptValue?: string | T;
};

export type Prompt =
  | TypedPrompt<'Text'>
  | TypedPrompt<'TextArea'>
  | TypedPrompt<'Date', Date>
  | TypedPrompt<'Money', number>
  | TypedPrompt<'CheckBox', boolean>
  | TypedPrompt<'Dropdown'>
  | TypedPrompt<'Number'>
  | TypedPrompt<'Radio'>;

export type PromptType = 'Text' | 'Date' | 'Money' | 'CheckBox' | 'Dropdown' | 'TextArea' | 'Number' | 'Radio';

export interface Borrower {
  applicationID?: number;
  borrowerID: number;
  email: string;
  filingStatus: string;
  firstName: string;
  homePhone: string;
  irsForm4506ID?: number;
  lastName: string;
  middleName: string;
  ofacScanComplete: boolean;
  okToEDisclose: boolean;
  signedStatusDate?: any;
  signedStatusDisplay: boolean;
  taxPeriodYear1?: string;
  taxPeriodYear2?: string;
  workPersonalPhone: string;
  taxForm?: string;
  whatDescribesYouBest?: string;
}

export interface VAInfo {
  vaLenderId?: string;
  lenderCompany?: string;
  lenderAddress: Address;
  everHadVaLoan?: boolean;
  vaOccupancyType: string;
  fhaReasonableValue?: number;
  fhaReasonableValueType: string;
  priceExceedingValueAwareness: string;
  fhaReceivedLeadPaintNotice?: boolean;
}
